import { IAddressLookupService } from "../services/interfaces/addressLookupService";
import { IMessagingService } from "../services/interfaces/messagingService";
import { FormComponent } from "./formComponent";
import { ILoggingService } from "../services/interfaces/loggingService";
import { IAnalyticsService, IHashingService, ISessionService } from "../services";
import { first } from "rxjs/operators";
import { IApiClient, SessionDto, ValidationError } from "@se/platform";
import { FormDto } from "@se/platform/forms/dtos/out/formDto";
import { ResponseDto } from "@se/platform/forms/dtos/out/responses/responseDto";
import { NewsLetterSignupDto } from "../models/dto/newsLetterSingupDto";
import { ValidationSummaryComponent } from "./validationSummaryComponent";
import { FormFieldComponent } from "./formFieldComponent";
import { hide, show } from "../utils/toggleVisibility";
import { AnalyticsEventName } from "../enums/analyticsEventName";
import { NewsletterSubmittedEvent } from "../models/analytics/newsletterSubmittedEvent";
import { NewsLetterFailureEvent } from "../models/analytics/newsLetterFailureEvent";
import { NewsLetterValidationErrorEvent } from "../models/analytics/newsLetterValidationError";
class NewsLetterFormComponent extends FormComponent {
    private accessToken: string;
    private responses: ResponseDto[];
    private submitBtn: HTMLButtonElement;
    private successUrl: string;
    private addressBookId: string;
    private errorMessageEl: HTMLElement;
    private formEl: HTMLElement;
    private validationSummary: ValidationSummaryComponent;
    private validationSummaryEl: HTMLElement;
    private analyticsTrackingService: IAnalyticsService;
    private dotDigitalDataFieldProps: HTMLElement[];
    private componentEl: HTMLElement;
    constructor(
        componentEl: HTMLElement,
        private apiClient: IApiClient,
        protected addressLookupService: IAddressLookupService,
        protected appInsights: ILoggingService,
        private sessionService: ISessionService,
        messagingService: IMessagingService,
        analyticsTrackingService: IAnalyticsService,
        private hashingService: IHashingService
        ) {
        super(componentEl, messagingService, addressLookupService, appInsights);
        this.componentEl = componentEl;
        this.analyticsTrackingService = analyticsTrackingService;
        this.submitBtn = componentEl.querySelector<HTMLButtonElement>(".js-newsletter-submit");
        this.formEl = componentEl.querySelector<HTMLElement>(".js-form");
        this.validationSummaryEl = document.querySelector<HTMLElement>(".js-validation-summary");
        this.errorMessageEl = componentEl.querySelector<HTMLElement>(".js-newsletter-error");

        this.sessionService.StartSession();
        const onSessionStarted$ = this.sessionService.OnSessionStarted$().pipe(first());
        onSessionStarted$.subscribe(async session =>
            await this.StartSession(session)
        );

        this.submitBtn.addEventListener("click", async (submitButtonEvent: Event) => {

            try {
                this.hideErrorMessage();
                submitButtonEvent.preventDefault(); // prevent refresh
                this.submitBtn.disabled = true; // prevent multiple clicks
                this.validationSummary.clearErrors();
                const fields = this.formFields.filter(formField => formField != undefined);
                this.responses = fields.map(f => f.getValue());
                this.successUrl = componentEl.dataset["formSuccessUrl"];

                this.addressBookId = componentEl.dataset["addressBookId"];

                if (this.addressBookId == null || this.addressBookId == "") {
                    return this.displayErrorMessage();
                }

                var submitResponse = await this.submitSignup();

                if (submitResponse.wasUnsuccessful) {
                    this.submitBtn.disabled = false; // re-enable to allow resubmission
                    if (submitResponse.validationErrors.length > 0) {
                        return this.displayValidationErrorMessages(submitResponse.validationErrors, fields);
                    }

                    // if there are no validation errors, then we have a general error
                    appInsights.LogException('newsletter form general error: ' + submitResponse.errorMessage);
                    this.analyticsTrackingService.RecordEvent(AnalyticsEventName.NewsletterSubmitError, new NewsLetterFailureEvent('Newsletter signup form general error: ' + submitResponse.errorMessage));
                    return this.displayErrorMessage();
                }

                var email = await this.hashingService.createHash(this.getResponseValue("email"));
                var firstName = await this.hashingService.createHash(this.getResponseValue("firstname"));
                var lastName = await this.hashingService.createHash(this.getResponseValue("lastname"));
                var postcode = this.getResponseValue("postcode").toString();

                this.analyticsTrackingService.RecordEvent(AnalyticsEventName.NewsletterSuccess, new NewsletterSubmittedEvent(
                    // need to normalise the hashed data
                    email.trim().toLowerCase(),
                    firstName.trim().toLowerCase(),
                    lastName.trim().toLowerCase(),
                    postcode
                ));

                window.location.href = this.successUrl;
                return;
            }
            catch (exception) {
                this.submitBtn.disabled = false; // re-enable to allow resubmission
                appInsights.LogException('newsletter form general error: ' + exception);
                this.analyticsTrackingService.RecordEvent(AnalyticsEventName.NewsletterSubmitError, new NewsLetterFailureEvent('Newsletter signup form general error: ' + exception));
                return this.displayErrorMessage();
            }
        });

        this.formEl.addEventListener("submit", (e: Event) => {
            this.submitBtn.dispatchEvent(new Event("click"));
            e.preventDefault();
        });

        this.formEl.addEventListener('focus', async (event) => {
            const target = event.target as HTMLElement;
            const parentContainer = target.parentElement.closest<HTMLElement>(".form-field");
            if (parentContainer && event.relatedTarget) {
                const relatedTarget = event.relatedTarget as HTMLElement;
                if (relatedTarget.closest<HTMLElement>('.js-validation-errors')) {
                    // We now know ...
                    // (a) a form-field has recieved focus, and
                    // (b) we got here from validation errors
                    // ... so trigger scroll parent .form-field into view to make label etc visible
                    await new Promise(res => setTimeout(res, 0)); // Safari scroll timing quirk fix
                    parentContainer.scrollIntoView();
                }
            }
        }, true);
    }

    private async StartSession(session: SessionDto) {
        this.accessToken = session.accessToken;
        if (this.accessToken == null) {
            this.displayErrorMessage();
        }
        this.validationSummary = new ValidationSummaryComponent(this.validationSummaryEl, this.messagingService, this);
        await super.LoadDynamicFormFieldComponents(session, new FormDto());
    }

    private async submitSignup() {
        var regularNewsSelected: boolean = this.getResponseValue("newsletter-preferences")?.toString().includes("Regular news and updates");
        var eventsAndWebinarsSelected: boolean = this.getResponseValue("newsletter-preferences")?.toString().includes("Monthly schedule of events and webinars");

        var signupDto = new NewsLetterSignupDto();
        signupDto.identifiers.email = this.getResponseValue("email");

        const dotDigitalDataFieldProps = this.componentEl.querySelectorAll<HTMLElement>(".js-is-dot-digital-data-field-prop");

        dotDigitalDataFieldProps.forEach(element => {
            let fieldId = element.dataset["fieldId"];
            signupDto.dataFields[fieldId] = this.getResponseValue(fieldId);
        });

        signupDto.lists.push(this.addressBookId);

        const consented = this.getResponseValue("consent");

        signupDto.consentRecords.push(
            {
                text: consented ? "I agree to the terms of the privacy notice and consent to receive marketing email updates from Scottish Enterprise." : "",
                dateTimeConsented: new Date()
            }
        );

        // SE specific
        // Only SE has checkboxlist types
        const checkboxListProps = this.componentEl.querySelectorAll<HTMLElement>(".js-is-checkboxlist");

        if (checkboxListProps.length > 0) {
            const prefs1: string[] = this.getResponseValue("newsletter-individual-preferences-1");
            const prefs2: string[] = this.getResponseValue("newsletter-individual-preferences-2");

            prefs1?.forEach(p => signupDto.preferences.push({ id: Number(p), isOptedIn: true }))
            prefs2?.forEach(p => signupDto.preferences.push({ id: Number(p), isOptedIn: true }))

            signupDto.dataFields.regular_news = regularNewsSelected ? "Yes" : "No";
            signupDto.dataFields.event_schedule = eventsAndWebinarsSelected ? "Yes" : "No";

            // can't select prefernces without regular news. how Marketing want it to work
            if (signupDto.preferences.length > 0 && signupDto.dataFields.regular_news !== "Yes") {
                signupDto.dataFields.regular_news = "Yes"
            }
        }

       return await this.apiClient.Post<NewsLetterSignupDto, NewsLetterSignupDto>("", "api/newslettersignup", signupDto, this.accessToken);
    }

    private async displayValidationErrorMessages(validationErrors: ValidationError[], fields: FormFieldComponent[]) {

        const errorsOrderedByField: ValidationError[] = [];
        // Go through form fields and compile applicable validation errors, in same order as fields
        fields.forEach(formField => {
           
            const validationErrorIndex = validationErrors.findIndex((validationError) => {
                return validationError.fieldName.replace(/\s+/g, "").toLowerCase() === formField.fieldId.replace(/\s+/g, "").toLowerCase();
            });

            // Match found, so add to ordered validation errors
            if (validationErrorIndex > -1) {
                const error = validationErrors[validationErrorIndex];

                if (formField.errorMessageCustom) {
                    error.errorMessage = formField.errorMessageCustom;
                }

                if (error.errorCode === "ContainsIllegalCharacters") {
                    error.errorMessage = "Please remove special characters like < >, and try again.";
                    // message is concatenation of other form fields so if there are any validation issues on other fields that will invalidate the message too
                    if (formField.fieldId.toLowerCase() == "message") {
                        error.errorMessage = "Please remove any special characters like < >, and check other fields too for < >, and try again.";
                    }
                }

                if (error.errorCode === "InvalidInput") {
                    const errorMessages = {
                        firstname: "First name can only contain letters and hyphens.",
                        lastname: "Last name can only contain letters, hyphens and apostrophes.",
                        compnay_name: "Company name can only contain letters and spaces.",
                        postcode: "Postcode can only contain letters, numbers and spaces.",
                    };
                    error.errorMessage = errorMessages[formField.fieldId.toLowerCase()];
                }

                formField.showError(error);
                errorsOrderedByField.push(error);
                // Remove from original validation errors array
                validationErrors.splice(validationErrorIndex, 1)
            }
        });
        // Add any leftover validation errors array to end of ordered errors and display
        this.validationSummary.showErrors(errorsOrderedByField.concat(validationErrors));
        await new Promise(res => setTimeout(res, 300)); // Optional - Introduce slight delay for NVDA screen-reader bug to prevent list being read twice
        this.validationSummary.focusErrors();

        //// push the amended list of validations errors to GTM
        this.analyticsTrackingService.RecordEvent(AnalyticsEventName.NewsletterFormValidationError, new NewsLetterValidationErrorEvent(errorsOrderedByField));

    }

    private displayErrorMessage() {
        show(this.errorMessageEl);
        this.errorMessageEl.scrollIntoView();
    }

    private hideErrorMessage() {
        hide(this.errorMessageEl);
    }

    private getResponseValue<T>(fieldId: string, defaultValue: T = null): T {
        const response = this.responses.find(response => response.fieldId === fieldId);
        if (response == null || response.value == null) {
            return defaultValue;
        }
        return response.value as T;
    }
}

export { NewsLetterFormComponent };